import {
  PROSPECT_COLUMNS,
  type ProspectColumnKey,
  type ProspectJurisdiction,
  cellText,
  filterSortJurisdictions,
  jurisdictionsCsv,
} from '@mollusk/shared';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { useMemo, useState } from 'react';

const NUMBERS: ReadonlySet<ProspectColumnKey> = new Set(
  PROSPECT_COLUMNS.filter((c) => c.numeric).map((c) => c.key),
);

function display(r: ProspectJurisdiction, key: ProspectColumnKey): string {
  const v = r[key];
  if (v == null || v === '') return '—';
  if (typeof v === 'number' && key !== 'pct_geocodeable' && key !== 'permits_per_10k') {
    return v.toLocaleString();
  }
  return cellText(r, key) || '—';
}

/**
 * The jurisdiction table prospects see on Coverage (#171): the kept columns,
 * each header sortable with a hover definition, a name/state/metro filter, and
 * a CSV of the filtered rows (plain names, FIPS, per-tag 10-year shares).
 */
export function CoverageJurisdictionTable({
  rows,
  onDownload,
}: {
  rows: readonly ProspectJurisdiction[];
  onDownload: (csv: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState<ProspectColumnKey>('population');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(50);
  const shown = useMemo(
    () => filterSortJurisdictions(rows, query, sortKey, dir),
    [rows, query, sortKey, dir],
  );
  const onSort = (key: ProspectColumnKey) => {
    if (key === sortKey) setDir(dir === 'asc' ? 'desc' : 'asc');
    else {
      setSortKey(key);
      setDir(NUMBERS.has(key) ? 'desc' : 'asc');
    }
    setPage(0);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          label="Filter by name, state or metro"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 280, flexGrow: { xs: 1, sm: 0 } }}
        />
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="outlined" onClick={() => onDownload(jurisdictionsCsv(shown))}>
          Download CSV
        </Button>
      </Box>
      <TableContainer component={Paper} sx={{ maxHeight: 640 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {PROSPECT_COLUMNS.map((c) => (
                <TableCell
                  key={c.key}
                  align={c.numeric ? 'right' : 'left'}
                  sortDirection={sortKey === c.key ? dir : false}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  <Tooltip title={c.definition} arrow describeChild>
                    <TableSortLabel
                      active={sortKey === c.key}
                      direction={sortKey === c.key ? dir : 'asc'}
                      onClick={() => onSort(c.key)}
                    >
                      {c.label}
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.slice(page * perPage, (page + 1) * perPage).map((r) => (
              <TableRow key={r.id} hover>
                {PROSPECT_COLUMNS.map((c) => (
                  <TableCell
                    key={c.key}
                    align={c.numeric ? 'right' : 'left'}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {display(r, c.key)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={shown.length}
        page={page}
        rowsPerPage={perPage}
        rowsPerPageOptions={[25, 50, 100, 250]}
        onPageChange={(_, p) => setPage(p)}
        onRowsPerPageChange={(e) => {
          setPerPage(Number(e.target.value));
          setPage(0);
        }}
      />
    </Box>
  );
}
